import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingCart, CreditCard, Truck, BarChart, Shield, Smartphone } from 'lucide-react';
import { PageSEO } from '@/components/SEO';

const funksjoner = [
  {
    icon: ShoppingCart,
    title: 'Brukervennlig handlekurv',
    description: 'Enkel og intuitiv kjøpsprosess som reduserer handlekurvfrafall.',
  },
  {
    icon: CreditCard,
    title: 'Betalingsløsninger',
    description: 'Integrasjon med Vipps, Klarna, kort og andre betalingsmetoder.',
  },
  {
    icon: Truck,
    title: 'Fraktintegrasjon',
    description: 'Automatisk fraktberegning med Posten, Bring og andre.',
  },
  {
    icon: BarChart,
    title: 'Salgsstatistikk',
    description: 'Full oversikt over salg, produkter og kundeadferd.',
  },
  {
    icon: Shield,
    title: 'Sikker handel',
    description: 'SSL-sertifikat og PCI-kompatible betalingsløsninger.',
  },
  {
    icon: Smartphone,
    title: 'Mobilvennlig',
    description: 'Optimalisert for handel på mobil og nettbrett.',
  },
];

const plattformer = [
  { name: 'WooCommerce', description: 'Fleksibel løsning basert på WordPress' },
  { name: 'Shopify', description: 'Skybasert plattform for enkel drift' },
  { name: 'Skreddersydd', description: 'Tilpassede løsninger for spesielle behov' },
];

export default function Nettbutikk() {
  return (
    <div>
      <PageSEO title="Nettbutikk – Selg dine produkter i hele Norge" path="/tjenester/nettbutikk" description="Vi utvikler profesjonelle nettbutikker med Vipps og Klarna for bedrifter i hele Norge. Øk salget med en moderne og mobilvennlig butikk." />
      {/* Hero */}
      <section className="pt-16 md:pt-20 pb-16 md:pb-20 bg-muted" data-testid="nettbutikk-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
                Tjeneste
              </p>
              <h1 className="font-syne font-bold text-5xl md:text-6xl text-primary tracking-tight leading-[0.95]">
                Nettbutikk som selger
              </h1>
              <p className="font-manrope text-lg text-muted-foreground mt-8 leading-relaxed">
                Vi utvikler profesjonelle nettbutikker som er raske, mobilvennlige og optimalisert for konvertering. Selg mer på nett med en butikk bygget for suksess.
              </p>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group mt-8"
                data-testid="nettbutikk-cta"
              >
                Start ditt nettbutikk-prosjekt
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
            <div className="relative group rounded-3xl overflow-hidden shadow-2xl ring-1 ring-primary/5">
              <div className="absolute -top-4 -right-4 w-full h-full border-2 border-accent/20 rounded-3xl" />
              <img
                src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80"
                alt="Nettbutikkløsninger for norske bedrifter – Side Kraft"
                className="relative z-10 w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Plattformer */}
      <section className="py-24 md:py-32" data-testid="nettbutikk-plattformer">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Plattformer
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight">
              Vi velger riktig plattform for deg
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plattformer.map((plattform, index) => (
              <div
                key={index}
                className="p-8 border border-border rounded-2xl hover:border-accent/50 transition-all duration-500 hover:shadow-lg group"
                data-testid={`plattform-${index}`}
              >
                <span className="font-syne font-bold text-4xl text-accent mb-4 block">0{index + 1}</span>
                <h3 className="font-syne font-bold text-xl text-primary mb-2">
                  {plattform.name}
                </h3>
                <p className="font-manrope text-sm text-muted-foreground">
                  {plattform.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Funksjoner */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground" data-testid="nettbutikk-funksjoner">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Funksjoner
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl tracking-tight">
              Alt du trenger for å selge på nett
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {funksjoner.map((funksjon, index) => (
              <div
                key={index}
                className="p-8 border border-primary-foreground/10 rounded-2xl hover:border-accent/50 transition-all duration-500 hover:shadow-lg group"
                data-testid={`funksjon-${index}`}
              >
                <funksjon.icon className="w-10 h-10 text-accent mb-6" />
                <h3 className="font-syne font-bold text-lg mb-3">
                  {funksjon.title}
                </h3>
                <p className="font-manrope text-sm text-primary-foreground/70 leading-relaxed">
                  {funksjon.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-muted" data-testid="nettbutikk-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight mb-6">
            Klar for å selge på nett?
          </h2>
          <p className="font-manrope text-muted-foreground max-w-xl mx-auto mb-10">
            Ta kontakt for en uforpliktende samtale om din nye nettbutikk.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group"
            data-testid="nettbutikk-bottom-cta"
          >
            Kontakt oss
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>
    </div>
  );
}
